import { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';
import { ConfirmModal } from '@demand-iq/ui';

interface Location {
  pathname: string;
  search: string;
  hash: string;
}

interface UnsavedChangesContextType {
  showUnsavedChangesModal: (onConfirm?: () => void) => void;
  isAnyFormDirty: boolean;
  setFormDirty: (formId: string, isDirty: boolean) => void;
  clearAllForms: () => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('journeys');
  const [showModal, setShowModal] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<(() => void) | null>(null);
  const [dirtyForms, setDirtyForms] = useState<Set<string>>(new Set());
  const isManualNavigationRef = useRef(false);

  const isAnyFormDirty = dirtyForms.size > 0;

  const setFormDirty = useCallback((formId: string, isDirty: boolean) => {
    setDirtyForms((prev) => {
      const newSet = new Set(prev);
      if (isDirty) {
        newSet.add(formId);
      } else {
        newSet.delete(formId);
      }
      return newSet;
    });
  }, []);

  const clearAllForms = useCallback(() => {
    setDirtyForms(new Set());
  }, []);

  const showUnsavedChangesModal = useCallback(
    (onConfirm?: () => void) => {
      if (isAnyFormDirty) {
        setPendingConfirm(() => onConfirm);
        setShowModal(true);
      } else {
        // If no unsaved changes, immediately execute the callback
        clearAllForms();
        onConfirm?.();
      }
    },
    [isAnyFormDirty, clearAllForms]
  );

  // Handle beforeunload event for browser navigation
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isAnyFormDirty) {
        event.preventDefault();
        return t('workflow-tracking-unsaved-changes-warning');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isAnyFormDirty, t]);

  // Handle React Router navigation
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
        return isAnyFormDirty && !isManualNavigationRef.current && currentLocation.pathname !== nextLocation.pathname;
      },
      [isAnyFormDirty]
    )
  );

  // Handle navigation blocking
  useEffect(() => {
    if (blocker.state === 'blocked' && !showModal) {
      setShowModal(true);
    }
  }, [blocker, showModal]);

  const handleConfirmNavigation = useCallback(async () => {
    // Close modal first
    setShowModal(false);

    // Set manual navigation flag to disable blocker
    isManualNavigationRef.current = true;

    // Clear all dirty forms since user is leaving
    clearAllForms();

    if (pendingConfirm) {
      pendingConfirm();
      setPendingConfirm(null);
    }

    // If blocker is in blocked state, proceed with the blocked navigation
    if (blocker.state === 'blocked') {
      blocker.proceed?.();
    }

    // Reset the flag after navigation
    setTimeout(() => {
      isManualNavigationRef.current = false;
    }, 100);
  }, [pendingConfirm, clearAllForms, blocker]);

  const handleCancelNavigation = useCallback(() => {
    blocker.reset?.();
    setShowModal(false);
    setPendingConfirm(null);
  }, [blocker]);

  return (
    <UnsavedChangesContext.Provider
      value={{
        showUnsavedChangesModal,
        isAnyFormDirty,
        setFormDirty,
        clearAllForms,
      }}
    >
      {children}
      <ConfirmModal
        open={showModal}
        onOpenChange={(open) => {
          if (!open) {
            handleCancelNavigation();
          }
        }}
        title={t('workflow-tracking-unsaved-changes-title', 'Unsaved Changes')}
        description={t('workflow-tracking-unsaved-changes-description', 'You have unsaved changes. Are you sure you want to leave?')}
        onConfirm={handleConfirmNavigation}
        confirmLabel={t('workflow-tracking-leave-without-saving', 'Leave Without Saving')}
        variant="destructive"
      />
    </UnsavedChangesContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUnsavedChanges() {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChanges must be used within an UnsavedChangesProvider');
  }
  return context;
}
